import React, { useMemo } from "react";
import { Container } from "Components/SideBar";
import { Divider, useMenuContext } from "Components";
import { useMenuBarHeight } from "hooks/useMenuBarHeight";
import { ReactComponent as DashboardIcon } from "assets/svg/dashboard2.svg";
import { Section } from "./Section";
import { RouteComponentProps, useRouteMatch } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as ChevronsLeftIcon } from "assets/svg/chevrons-left.svg";
import { ReactComponent as MusicIcon } from "assets/svg/music.svg";
import { ReactComponent as CirclePlusIcon } from "assets/svg/circle-plus.svg";
import { ReactComponent as PastShowsIcon } from "assets/svg/past-shows.svg";
import { ReactComponent as UpcomingShowsIcon } from "assets/svg/upcoming-shows.svg";
import { ReactComponent as CalendarIcon } from "assets/svg/calendar-dates.svg";
import { ReactComponent as MicrophoneIcon } from "assets/svg/microphone.svg";
import { ReactComponent as ClipboardCheckIcon } from "assets/svg/clipboard-check.svg";
import { ReactComponent as ClockAltIcon } from "assets/svg/clock-alt.svg";
import { ReactComponent as SmartphoneIcon } from "assets/svg/smartphone.svg";
import { ReactComponent as UserIcon } from "assets/svg/user.svg";
import { ReactComponent as ListIcon } from "assets/svg/list.svg";
import { ReactComponent as TrashIcon } from "assets/svg/trash.svg";
import { ReactComponent as GlobeIcon } from "assets/svg/globe.svg";
import BarChartIcon from "svg/BarChartIcon";
import { ReactComponent as CopyAltIcon } from "assets/svg/copy-alt.svg";
import { SectionItem } from "./Section/Item";
import { SvgLink } from "melodies-source/Svgs/Link";
import { ReactComponent as HandshakeIcon } from "assets/svg/handshake-alt.svg";
import { ReactComponent as PreSaveReleaseIcon } from "assets/svg/presave-release.svg";
import { SvgConnected } from "melodies-source/Svgs/Connected";
import { SvgErrorAlt } from "melodies-source/Svgs/ErrorAlt";
import { ReactComponent as FinanceIcon } from "assets/svg/finance.svg";
import { useUser } from "auth";
import { hasAccessToRoute } from "../utils";
import { ReactComponent as GearIcon } from "assets/svg/gear.svg";
import { UserClaims } from "auth/types";
import { Profile } from "@max/common/user";

export type Section = {
  title?: string;
  items: SectionItem[];
  collapseable?: boolean;
};

export const sections = (match: RouteComponentProps["match"]): Section[] => [
  {
    title: "General",
    items: [
      {
        title: "Primary Dashboard",
        description: "Main dashboard",
        icon: <DashboardIcon />,
        path: `${match.path}/dashboard`,
      },
      {
        title: "Artists",
        description: "List of artists",
        icon: <MusicIcon />,
        path: `${match.path}/artists`,
      },
    ],
  },
  {
    title: "SET.Live",
    items: [
      {
        title: "Bookings + Usage",
        description: "Bookings",
        icon: <MicrophoneIcon />,
        path: `${match.path}/bookings`,
      },
      {
        title: "Recent Shows",
        description: "List of events",
        icon: <PastShowsIcon />,
        path: `${match.path}/recent-shows`,
      },
      {
        title: "Upcoming Shows",
        description: "List of events",
        icon: <UpcomingShowsIcon />,
        path: `${match.path}/upcoming-shows`,
      },
      {
        title: "Multi-Day Events",
        description: "List of events",
        icon: <CalendarIcon />,
        path: `${match.path}/multiday-events`,
      },
    ],
  },
  {
    title: "SET.Fan",
    items: [
      {
        title: "Usage Overview",
        description: "Trends from your campaigns",
        icon: <ClipboardCheckIcon />,
        path: `${match.path}/campaign-overview`,
      },
      {
        title: "Recent Campaigns",
        description: "Your most recent SET.Fan campaigns",
        icon: <ClockAltIcon />,
        path: `${match.path}/recent-campaigns`,
      },
    ],
  },
  {
    title: "SET.Bio",
    items: [
      {
        title: "SET.Bio Usage",
        description: "Trends from your Bio",
        icon: <SmartphoneIcon />,
        path: `${match.path}/bio-usage`,
      },
    ],
  },
  {
    items: [
      {
        title: "Users",
        description: "Users from all platforms",
        icon: <UserIcon />,
        path: `${match.path}/users`,
      },
      {
        title: "Pre-Save Campaigns",
        description: "List of pre-save campaigns",
        icon: <PreSaveReleaseIcon />,
        path: `${match.path}/pre-save-campaigns`,
      },
      {
        title: "Brand Preferences",
        description: "View artist brand preferences report",
        icon: <HandshakeIcon />,
        path: `${match.path}/brand-preferences`,
      },
      {
        title: "Finance",
        description: "Financial reports",
        icon: <FinanceIcon />,
        path: `${match.path}/finance`,
        groups: ["finance"],
      },
    ],
  },
  {
    title: "Admin Functions",
    items: [
      {
        title: "New Artist Signup",
        description: "List of requests",
        icon: <CirclePlusIcon />,
        path: `${match.path}/signup-requests`,
      },
      {
        title: "Rules",
        description: "Rules",
        icon: <SvgLink />,
        path: `${match.path}/rules`,
      },
      {
        title: "Integration Sync Report",
        description: "Report of integration sync results",
        icon: <SvgConnected />,
        path: `${match.path}/integration-sync`,
      },
      {
        title: "Integration Sync Errors",
        description: "Report of errors related to the internal sync process",
        icon: <SvgErrorAlt />,
        path: `${match.path}/integration-sync-errors`,
      },
      {
        title: "Custom Claims",
        description: "List of MAX users",
        icon: <GearIcon />,
        path: `${match.path}/custom-claims`,
      },
      {
        title: "Accessed Fans",
        icon: <ListIcon />,
        path: `${match.path}/accessed-fans`,
        comingSoon: true,
      },
      {
        title: "Data Deletion Requests",
        description: "Manage user data deletion requests",
        icon: <TrashIcon />,
        path: `${match.path}/data-deletion-requests`,
      },
      {
        title: "API Monitoring",
        icon: <GlobeIcon />,
        path: `${match.path}/monitor`,
        comingSoon: true,
      },
      {
        title: "Admin User Report",
        icon: <StyledBarChartIcon />,
        path: `${match.path}/user-report`,
        comingSoon: true,
      },
      {
        title: "Duplicate Events",
        icon: <CopyAltIcon />,
        path: `${match.path}/duplicate-event`,
        comingSoon: true,
      },
    ],
  },
];

export const getAllowedSections = (
  claims: UserClaims,
  profile: Profile,
  match: RouteComponentProps["match"],
) => {
  if (!profile) return [];

  const isSuperAdmin = claims.admin;
  const isAdmin = profile.groups?.includes("admin");

  if (isSuperAdmin || isAdmin) return sections(match);

  return sections(match)
    .map((section) => ({
      ...section,
      items: section.items.filter((route) => hasAccessToRoute(route, profile)),
    }))
    .filter((section) => section.items.length > 0);
};

export const SideBar: React.FC = () => {
  const match = useRouteMatch();
  const { menuMode, toggleMenuMode, isMobile } = useMenuContext();
  const { menuBarHeight } = useMenuBarHeight();
  const { claims, profile } = useUser();

  const allowedSections = useMemo(
    () => getAllowedSections(claims, profile, match),
    [claims?.admin, profile?.groups, match],
  );

  return (
    <StyledContainer menuMode={menuMode} menuBarHeight={menuBarHeight}>
      {!isMobile && (
        <MenuModeControl data-menu-mode={menuMode} onClick={toggleMenuMode}>
          <ChevronsLeftIcon />
          {menuMode === "expanded" && <p>Collapse</p>}
        </MenuModeControl>
      )}
      {allowedSections.map((section, index) => (
        <React.Fragment key={`section-${section.title}`}>
          {index > 0 && <StyledDivider />}
          <Section key={section.title} section={section} />
        </React.Fragment>
      ))}
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)`
  padding-top: 10px;
`;

const MenuModeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  margin-top: 10px;

  p {
    font-weight: 600;
  }

  svg {
    border-radius: 6px;
    border: 2px solid black;
    height: 25px;
    width: fit-content;
    margin-left: 20px;
  }

  &[data-menu-mode="collapsed"] {
    margin-bottom: 20px;

    svg {
      height: 30px;
      margin: auto;
      transform: rotate(180deg);
    }
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0px;
`;

const StyledBarChartIcon = styled(BarChartIcon)`
  & > path {
    fill: currentColor;
  }
`;
